/**
 * Flat Service Data Service
 *
 * Simplified service management with flat structure:
 * Category → Services (no subcategories/variations complexity)
 *
 * This is the unified service data layer for all apps.
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  Timestamp,
  writeBatch,
  query,
  orderBy
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../config/firebase';
import { serviceDisplaySettings } from './serviceDisplaySettings';
import { defaultServiceStructure } from '../data/defaultServices';

// ============================================
// INTERFACES - Simplified Flat Structure
// ============================================

export interface Service {
  id: string;
  name: string;
  description: string;
  price: number;
  duration: number;           // in minutes
  imageUrl?: string;
  active: boolean;
  emergencyEligible?: boolean;
  allowEmergencySqueeze?: boolean;
}

export interface ServiceCategory {
  id: string;
  name: string;
  description: string;
  icon: string;               // emoji or icon identifier
  services: Service[];        // Flat array of services (no subcategories)
  active: boolean;
  sortOrder: number;
  createdAt: Date;
  updatedAt: Date;
}

// For UI components that need a flat list with category info
export interface FlatServiceItem {
  id: string;
  name: string;
  description: string;
  price: number;
  duration: number;
  imageUrl?: string;
  active: boolean;
  categoryId: string;
  categoryName: string;
  categoryIcon: string;
  emergencyEligible?: boolean;
}

// ============================================
// SERVICE CLASS
// ============================================

class FlatServiceDataService {
  private categoriesCollection = collection(db, 'service_categories');
  private cachedCategories: ServiceCategory[] = [];
  private listeners: ((categories: ServiceCategory[]) => void)[] = [];
  private unsubscribe: (() => void) | null = null;
  private seedAttempted = false;

  constructor() {
    this.initializeSubscription();
    // RESILIENCE (Jose 2026-10-29 audit): subscribes at module load (pre-auth) → re-subscribe once
    // auth is ready so a denied cold read self-heals (unsub prior first). See shiftService fix.
    try { onAuthStateChanged(auth, (u) => { if (u) this.initializeSubscription(); }); } catch { /* ignore */ }
  }

  private initializeSubscription() {
    try { this.unsubscribe?.(); } catch { /* ignore */ }
    this.unsubscribe = null;
    const q = query(this.categoriesCollection, orderBy('sortOrder', 'asc'));

    this.unsubscribe = onSnapshot(q, { includeMetadataChanges: true }, async (snapshot) => {
      // Auto-seed defaults ONLY for a genuinely new, empty shop.
      // LANDMINE FIXED (Jose 2026-10-23): the FIRST onSnapshot fires from the LOCAL cache, and on a cold
      // cache (fresh bundle / new device / cleared storage) it is EMPTY before the server data syncs.
      // The old guard checked only `snapshot.empty`, so a cold-cache load SEEDED a default set ON TOP of
      // the shop's real categories → 3 duplicate "Haircuts/Beard/Specialty" docs with placeholder prices
      // (the 2026-10-23 13:11 incident). We now require a SERVER-confirmed empty (`!fromCache`) before
      // seeding, so a transient cache-empty can never duplicate real data.
      if (snapshot.empty && !snapshot.metadata.fromCache && !this.seedAttempted) {
        this.seedAttempted = true;
        console.log('Service categories collection is empty (server-confirmed) — seeding with default data...');
        await this.seedDefaultServices();
        // onSnapshot will fire again after seed writes, so return early
        return;
      }

      this.cachedCategories = snapshot.docs.map(doc => this.mapDocToCategory(doc));

      // Apply custom order from settings if available
      this.applyCustomOrder();

      // Notify listeners
      this.listeners.forEach(listener => listener(this.cachedCategories));
    }, (error) => {
      console.error('Error listening to service categories:', error);
    });
  }

  /**
   * Seed the serviceCategories collection with default SLIK services.
   * Converts the hierarchical defaultServiceStructure into flat format.
   * Only runs once when the collection is empty (first-time setup / migration).
   */
  private async seedDefaultServices(): Promise<void> {
    try {
      const batch = writeBatch(db);

      for (const category of defaultServiceStructure) {
        // Flatten hierarchical subcategories → flat services array
        const services: Service[] = [];

        if (category.subcategories) {
          for (const sub of category.subcategories) {
            for (const variation of (sub.variations || [])) {
              services.push({
                id: variation.id,
                name: variation.name,
                description: variation.description || '',
                price: variation.price,
                duration: variation.duration,
                imageUrl: variation.imageUrl,
                active: variation.active !== false,
                emergencyEligible: variation.emergencyEligible,
                allowEmergencySqueeze: variation.allowEmergencySqueeze,
              });
            }
          }
        }

        // Also handle flat variations directly on the category (if any)
        if ((category as any).variations) {
          for (const variation of (category as any).variations) {
            services.push({
              id: variation.id,
              name: variation.name,
              description: variation.description || '',
              price: variation.price,
              duration: variation.duration,
              imageUrl: variation.imageUrl,
              active: variation.active !== false,
              emergencyEligible: variation.emergencyEligible,
              allowEmergencySqueeze: variation.allowEmergencySqueeze,
            });
          }
        }

        const newDocRef = doc(this.categoriesCollection);
        batch.set(newDocRef, {
          name: category.name,
          description: category.description || '',
          icon: category.icon || '✂️',
          services,
          active: category.active !== false,
          sortOrder: category.sortOrder || 0,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
      }

      await batch.commit();
      console.log(`Seeded ${defaultServiceStructure.length} service categories into Firebase.`);
    } catch (error) {
      console.error('Error seeding default services:', error);
    }
  }

  private mapDocToCategory(doc: any): ServiceCategory {
    const data = doc.data();

    // Handle both old hierarchical and new flat structures
    let services: Service[] = [];

    if (data.services) {
      // New flat structure
      services = data.services;
    } else if (data.isFlat && data.variations) {
      // Old flat structure (variations directly on category)
      services = data.variations.map((v: any) => ({
        id: v.id,
        name: v.name,
        description: v.description || '',
        price: v.price,
        duration: v.duration,
        imageUrl: v.imageUrl,
        active: v.active !== false,
        emergencyEligible: v.emergencyEligible,
        allowEmergencySqueeze: v.allowEmergencySqueeze,
      }));
    } else if (data.subcategories) {
      // Old hierarchical structure - flatten it
      services = data.subcategories.flatMap((sub: any) =>
        (sub.variations || []).map((v: any) => ({
          id: v.id,
          name: v.name, // Jose 2026-10-19: variation name only — subcategory is the group header, no redundant prefix
          description: v.description || '',
          price: v.price,
          duration: v.duration,
          imageUrl: v.imageUrl,
          active: v.active !== false && sub.active !== false,
          emergencyEligible: v.emergencyEligible,
          allowEmergencySqueeze: v.allowEmergencySqueeze,
        }))
      );
    }

    return {
      id: doc.id,
      name: data.name,
      description: data.description || '',
      icon: data.icon || '✂️',
      services,
      active: data.active !== false,
      sortOrder: data.sortOrder || 0,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    };
  }

  private async applyCustomOrder() {
    const settings = serviceDisplaySettings.getSettingsSync();
    if (settings.categoryOrder && settings.categoryOrder.length > 0) {
      const orderMap = new Map(settings.categoryOrder.map((id, index) => [id, index]));
      this.cachedCategories.sort((a, b) => {
        const orderA = orderMap.get(a.id) ?? a.sortOrder + 1000;
        const orderB = orderMap.get(b.id) ?? b.sortOrder + 1000;
        return orderA - orderB;
      });
    }
  }

  // ============================================
  // READ OPERATIONS
  // ============================================

  /**
   * Get all categories (including inactive) - for admin
   */
  async getAllCategories(): Promise<ServiceCategory[]> {
    if (this.cachedCategories.length > 0) {
      return this.cachedCategories;
    }

    const q = query(this.categoriesCollection, orderBy('sortOrder', 'asc'));
    const snapshot = await getDocs(q);
    this.cachedCategories = snapshot.docs.map(doc => this.mapDocToCategory(doc));
    await this.applyCustomOrder();
    return this.cachedCategories;
  }

  /**
   * Get only active categories with active services - for customer-facing
   */
  async getActiveCategories(): Promise<ServiceCategory[]> {
    const all = await this.getAllCategories();
    return all
      .filter(cat => cat.active)
      .map(cat => ({
        ...cat,
        services: cat.services.filter(s => s.active)
      }))
      .filter(cat => cat.services.length > 0);
  }

  /**
   * Get a single category by ID
   */
  async getCategoryById(id: string): Promise<ServiceCategory | null> {
    const docSnap = await getDoc(doc(this.categoriesCollection, id));
    if (!docSnap.exists()) return null;
    return this.mapDocToCategory(docSnap);
  }

  /**
   * Get all services across all categories (convenience method)
   */
  async getAllServices(): Promise<Service[]> {
    const categories = await this.getAllCategories();
    return categories.flatMap(cat => cat.services);
  }

  /**
   * Get all services as a flat list (with category info)
   */
  async getFlatServiceList(activeOnly: boolean = true): Promise<FlatServiceItem[]> {
    const categories = activeOnly
      ? await this.getActiveCategories()
      : await this.getAllCategories();

    return categories.flatMap(cat =>
      cat.services
        .filter(s => !activeOnly || s.active)
        .map(s => ({
          ...s,
          categoryId: cat.id,
          categoryName: cat.name,
          categoryIcon: cat.icon,
        }))
    );
  }

  /**
   * Get a single service by ID
   */
  async getServiceById(serviceId: string): Promise<FlatServiceItem | null> {
    const services = await this.getFlatServiceList(false);
    return services.find(s => s.id === serviceId) || null;
  }

  /**
   * Subscribe to category changes
   */
  subscribe(callback: (categories: ServiceCategory[]) => void): () => void {
    this.listeners.push(callback);

    // Immediately call with current data
    if (this.cachedCategories.length > 0) {
      callback(this.cachedCategories);
    }

    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  // ============================================
  // WRITE OPERATIONS
  // ============================================

  /**
   * Add a new category
   */
  async addCategory(category: Omit<ServiceCategory, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const docRef = await addDoc(this.categoriesCollection, {
      ...category,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    return docRef.id;
  }

  /**
   * Update a category
   */
  async updateCategory(id: string, updates: Partial<ServiceCategory>): Promise<void> {
    const docRef = doc(this.categoriesCollection, id);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.now(),
    });
  }

  /**
   * Delete a category
   */
  async deleteCategory(id: string): Promise<void> {
    await deleteDoc(doc(this.categoriesCollection, id));
  }

  /**
   * Add a service to a category
   */
  async addService(categoryId: string, service: Omit<Service, 'id'>): Promise<void> {
    const category = await this.getCategoryById(categoryId);
    if (!category) throw new Error('Category not found');

    const newService: Service = {
      ...service,
      id: `${categoryId}-${Date.now()}`,
    };

    await this.updateCategory(categoryId, {
      services: [...category.services, newService],
    });
  }

  /**
   * Update a service within a category
   */
  async updateService(categoryId: string, serviceId: string, updates: Partial<Service>): Promise<void> {
    const category = await this.getCategoryById(categoryId);
    if (!category) throw new Error('Category not found');

    const updatedServices = category.services.map(s =>
      s.id === serviceId ? { ...s, ...updates } : s
    );

    await this.updateCategory(categoryId, { services: updatedServices });
  }

  /**
   * Delete a service from a category
   */
  async deleteService(categoryId: string, serviceId: string): Promise<void> {
    const category = await this.getCategoryById(categoryId);
    if (!category) throw new Error('Category not found');

    await this.updateCategory(categoryId, {
      services: category.services.filter(s => s.id !== serviceId),
    });
  }

  /**
   * Remove a service from a category (alias for deleteService)
   */
  async removeService(categoryId: string, serviceId: string): Promise<void> {
    return this.deleteService(categoryId, serviceId);
  }

  /**
   * Toggle service active status
   */
  async toggleServiceActive(categoryId: string, serviceId: string, active: boolean): Promise<void> {
    await this.updateService(categoryId, serviceId, { active });
  }

  /**
   * Toggle category active status
   */
  async toggleCategoryActive(categoryId: string, active: boolean): Promise<void> {
    await this.updateCategory(categoryId, { active });
  }

  /**
   * Reorder categories (update sortOrder for all)
   */
  async reorderCategories(categoryIds: string[]): Promise<void> {
    const batch = writeBatch(db);

    categoryIds.forEach((id, index) => {
      const docRef = doc(this.categoriesCollection, id);
      batch.update(docRef, {
        sortOrder: index,
        updatedAt: Timestamp.now(),
      });
    });

    await batch.commit();

    // Also save to display settings for custom ordering
    await serviceDisplaySettings.updateCategoryOrder(categoryIds);
  }

  /**
   * Reorder services within a category
   */
  async reorderServices(categoryId: string, serviceIds: string[]): Promise<void> {
    const category = await this.getCategoryById(categoryId);
    if (!category) throw new Error('Category not found');

    const serviceMap = new Map(category.services.map(s => [s.id, s]));
    const reorderedServices = serviceIds
      .map(id => serviceMap.get(id))
      .filter((s): s is Service => s !== undefined);

    await this.updateCategory(categoryId, { services: reorderedServices });
  }

  // ============================================
  // MIGRATION HELPER
  // ============================================

  /**
   * Migrate old hierarchical structure to flat
   * Call this once to clean up the database
   */
  async migrateToFlatStructure(): Promise<{ migrated: number; errors: string[] }> {
    const snapshot = await getDocs(this.categoriesCollection);
    let migrated = 0;
    const errors: string[] = [];

    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();

      // Skip if already migrated (has 'services' array)
      if (data.services) continue;

      try {
        let services: Service[] = [];

        if (data.isFlat && data.variations) {
          // Convert variations to services
          services = data.variations.map((v: any) => ({
            id: v.id,
            name: v.name,
            description: v.description || '',
            price: v.price,
            duration: v.duration,
            imageUrl: v.imageUrl,
            active: v.active !== false,
            emergencyEligible: v.emergencyEligible,
            allowEmergencySqueeze: v.allowEmergencySqueeze,
          }));
        } else if (data.subcategories) {
          // Flatten hierarchical structure
          services = data.subcategories.flatMap((sub: any) =>
            (sub.variations || []).map((v: any) => ({
              id: v.id,
              name: v.name,
              description: v.description || '',
              price: v.price,
              duration: v.duration,
              imageUrl: v.imageUrl,
              active: v.active !== false && sub.active !== false,
              emergencyEligible: v.emergencyEligible,
              allowEmergencySqueeze: v.allowEmergencySqueeze,
            }))
          );
        }

        // Update document with new flat structure
        await updateDoc(doc(this.categoriesCollection, docSnap.id), {
          services,
          // Remove old fields
          isFlat: null,
          variations: null,
          subcategories: null,
          updatedAt: Timestamp.now(),
        });

        migrated++;
      } catch (error) {
        errors.push(`Failed to migrate category ${docSnap.id}: ${error}`);
      }
    }

    return { migrated, errors };
  }
}

export const flatServiceDataService = new FlatServiceDataService();
export default flatServiceDataService;
